import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { IAzureReservations } from '../../../services/azure/azureReservations/azureReservations.model';
import { RootState } from '../../app.model';
import {
  searchAzureReservations,
  getAzureReservationsById,
  saveAzureReservations,
  deleteAzureReservations,
} from './azureReservations.action';
import { IAzureReservationsState } from './azureReservations.model';

export const initialState: IAzureReservationsState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const azureReservationsSlice = createSlice({
  name: 'azureReservations',
  initialState,
  reducers: {
    clearAzureReservations: () => {
      return initialState;
    },
    clearAzureReservationsMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearAzureReservationsGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchAzureReservations.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchAzureReservations.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IAzureReservations>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchAzureReservations.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getAzureReservationsById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getAzureReservationsById.fulfilled.type]: (
      state,
      action: PayloadAction<IAzureReservations>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getAzureReservationsById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveAzureReservations.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveAzureReservations.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveAzureReservations.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteAzureReservations.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteAzureReservations.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteAzureReservations.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const azureReservationsSelector = (state: RootState) => state.azureReservations;

// Actions
export const {
  clearAzureReservations,
  clearAzureReservationsMessages,
  clearAzureReservationsGetById,
  setTableColumnSelection,
} = azureReservationsSlice.actions;

// The reducer
export default azureReservationsSlice.reducer;
